// Single source of truth for the hotkey cheatsheet.
//
// Each surface registers the keys it actually wires, co-located with the
// keydown handler. The cheatsheet (hotkeyHint) renders straight from this
// registry, so the two can never drift: if a key isn't registered it isn't
// shown, and if it's rebound you edit exactly one place — next to the
// handler. (The old hardcoded list had bogus V / R entries for keys that
// were never wired; a registry makes that class of bug impossible.)
//
// `register()` returns an unregister fn so a surface drops its rows on
// dispose / HMR teardown, keeping the cheatsheet free of stale duplicates.

export interface HotkeyEntry {
  /** Display form, e.g. 'Space', '[ / ]', '⌘Z / Ctrl-Z'. */
  keys: string;
  description: string;
  /** Free-form; known sections sort first in SECTION_ORDER, then the rest. */
  section: string;
  /** Sort order within the section (defaults to registration order). */
  order?: number;
}

const SECTION_ORDER = ['navigation', 'editing', 'dev HUD'];

class HotkeyRegistry {
  private entries: Array<HotkeyEntry & { seq: number }> = [];
  private seq = 0;

  register(entry: HotkeyEntry): () => void {
    const e = { ...entry, seq: this.seq++ };
    this.entries.push(e);
    return () => {
      const i = this.entries.indexOf(e);
      if (i >= 0) this.entries.splice(i, 1);
    };
  }

  /** Grouped + ordered for rendering. Known sections come first in
   *  SECTION_ORDER; any others follow in first-seen order. */
  sections(): Array<{ title: string; rows: Array<[string, string]> }> {
    const groups = new Map<string, Array<HotkeyEntry & { seq: number }>>();
    for (const e of this.entries) {
      const arr = groups.get(e.section) ?? [];
      arr.push(e);
      groups.set(e.section, arr);
    }
    const titles = [
      ...SECTION_ORDER.filter((s) => groups.has(s)),
      ...[...groups.keys()].filter((s) => !SECTION_ORDER.includes(s)),
    ];
    return titles.map((title) => ({
      title,
      rows: groups
        .get(title)!
        .slice()
        .sort((a, b) => (a.order ?? a.seq) - (b.order ?? b.seq))
        .map((e) => [e.keys, e.description] as [string, string]),
    }));
  }
}

/** Process-wide singleton, mirroring the devBus pattern. */
export const hotkeyRegistry = new HotkeyRegistry();
